import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Box,
} from '@mui/material';
import { CloudDoneOutlinedIcon as CloudDoneOutlined, CloudOffOutlinedIcon as CloudOffOutlined, LayersIcon as Layers, SignalCellularAltIcon as SignalCellularAlt, SpeedIcon as Speed, StorageIcon as Storage } from "../../../icons";
import { useRootStore } from '../../../store/contexts';

export const OverviewTab: React.FC = observer(() => {
  const rootStore = useRootStore();
  const monitorStore = rootStore.systemMonitorStore;
  const activityStore = rootStore.activityStore;
  const langStore = rootStore.langStore;

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getStatusColor = (status: 'healthy' | 'warning' | 'critical' | 'error'): 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
      case 'error':
      default:
        return 'error';
    }
  };

  const connectionStatus = monitorStore.connectionStatus;
  const storageStatus = monitorStore.storageStatus;
  const performanceScore = monitorStore.performanceScore;

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {connectionStatus === 'healthy' ? (
                  <CloudDoneOutlined color="success" sx={{ mr: 1 }} />
                ) : (
                  <CloudOffOutlined color="error" sx={{ mr: 1 }} />
                )}
                <Typography variant="h6">{langStore.monitorConnection}</Typography>
              </Box>
              <Chip
                label={
                  rootStore.eventSourceStore.connectionState === 'connected'
                    ? langStore.monitorConnected
                    : rootStore.eventSourceStore.connectionState === 'connecting'
                    ? langStore.monitorConnecting
                    : langStore.monitorDisconnected
                }
                color={getStatusColor(connectionStatus)}
                size="small"
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorNetworkLabel}: {monitorStore.networkInfo.online ? langStore.monitorOnline : langStore.monitorOffline}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorType}: {monitorStore.networkInfo.effectiveType}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SignalCellularAlt color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorActivity}</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {activityStore.activeCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {langStore.monitorActiveOperations}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${langStore.monitorQueries}: ${activityStore.getActivityCount('query')}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${langStore.monitorMutations}: ${activityStore.getActivityCount('mutation')}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed
                  color={performanceScore >= 80 ? 'success' : performanceScore >= 50 ? 'warning' : 'error'}
                  sx={{ mr: 1 }}
                />
                <Typography variant="h6">{langStore.monitorPerformance}</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {performanceScore}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {langStore.monitorPerformanceScore}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${langStore.monitorLCP}: ${monitorStore.webVitals.LCP.toFixed(0)}${langStore.monitorMs}`}
                  size="small"
                  variant="outlined"
                  color={monitorStore.webVitals.LCP < 2500 ? 'success' : 'warning'}
                />
                <Chip
                  label={`${langStore.monitorCLS}: ${monitorStore.webVitals.CLS.toFixed(3)}`}
                  size="small"
                  variant="outlined"
                  color={monitorStore.webVitals.CLS < 0.1 ? 'success' : 'warning'}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Storage color={getStatusColor(storageStatus)} sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorStorage}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formatBytes(monitorStore.storageInfo.usage)} / {formatBytes(monitorStore.storageInfo.quota)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={monitorStore.storageInfo.percentUsed}
                color={getStatusColor(storageStatus)}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {monitorStore.storageInfo.percentUsed}{langStore.monitorPercentUsed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Layers color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorStores}</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {rootStore.storeRegistry.allStores.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorActiveStores}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorNetworkSpeed}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorDownlink}: {monitorStore.networkInfo.downlink.toFixed(2)} {langStore.monitorMbps}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {langStore.monitorRTT}: {monitorStore.networkInfo.rtt} {langStore.monitorMs}
              </Typography>
              {monitorStore.networkInfo.saveData && (
                <Chip label={langStore.monitorDataSaverOn} size="small" color="info" sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
});
